import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';

const AboutSection = () => {
  const companyStats = [
    {
      icon: "Calendar",
      value: "12+",
      label: "Years of Excellence",
      description: "Serving Malaysia since 2012"
    },
    {
      icon: "Users",
      value: "500+",
      label: "Medical Professionals",
      description: "Trusted by leading surgeons"
    },
    {
      icon: "Package",
      value: "1000+",
      label: "Products Distributed",
      description: "Comprehensive medical solutions"
    },
    {
      icon: "Award",
      value: "100%",
      label: "GDPMD Compliance",
      description: "Certified quality assurance"
    }
  ];

  const teamMembers = [
    {
      name: "Dr. Ahmad Rahman",
      position: "Medical Director",
      image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop&crop=face",
      expertise: "Gastroenterology & Endoscopy"
    },
    {
      name: "Sarah Lim",
      position: "Operations Manager",
      image: "https://images.unsplash.com/photo-1594824388853-d0c7b3b9e2c5?w=400&h=400&fit=crop&crop=face",
      expertise: "Medical Device Distribution"
    },
    {
      name: "Dr. Raj Patel",
      position: "Technical Advisor",
      image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=400&fit=crop&crop=face",
      expertise: "Surgical Instruments"
    }
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            About Endosurgery Malaysia
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Established in 2012, Endosurgery Sdn Bhd has been at the forefront of medical technology 
            distribution in Malaysia, providing healthcare professionals with cutting-edge endoscopic 
            and surgical solutions.
          </p>
        </motion.div>

        {/* Company Story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <Card className="bg-gradient-to-r from-muted/50 to-primary/5 border-0">
            <CardContent className="p-8 md:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={24} color="white" />
                </div>
                <h3 className="text-2xl font-bold text-text-primary">Our Journey</h3>
              </div>
              <p className="text-text-secondary leading-relaxed">
                Founded with a vision to revolutionize medical care in Malaysia, we have grown from a 
                small startup to become the nation's most trusted distributor of endoscopic and surgical 
                equipment. Our commitment to quality, innovation, and customer service has earned us the 
                trust of over 500 medical professionals across the country.
              </p>
                             <div className="flex items-center space-x-4 pt-4">
                 <motion.div 
                   whileHover={{ scale: 1.05, y: -2 }}
                   className="flex items-center space-x-2 px-4 py-2 rounded-full border border-border/30 shadow-lg bg-gradient-to-r from-green-600 to-green-500 backdrop-blur-md"
                 >
                   <Icon name="Shield" size={16} className="text-white" />
                   <span className="text-white font-semibold text-sm">GDPMD Certified</span>
                 </motion.div>
                 <motion.div 
                   whileHover={{ scale: 1.05, y: -2 }}
                   className="flex items-center space-x-2 px-4 py-2 rounded-full border border-border/30 shadow-lg bg-gradient-to-r from-purple-600 to-purple-500 backdrop-blur-md"
                 >
                   <Icon name="CheckCircle" size={16} className="text-white" />
                   <span className="text-white font-semibold text-sm">ISO 13485</span>
                 </motion.div>
               </div>
            </div>
            <div className="relative">
              <Image
                src="/assets/images/about.jpg"
                alt="Modern medical facility"
                className="rounded-xl shadow-modern w-full h-80 object-cover"
              />
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="absolute -bottom-6 -right-6 backdrop-blur-modern p-4 rounded-xl shadow-modern-hover border border-border/50"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">2012</div>
                  <div className="text-sm text-text-secondary">Founded</div>
                </div>
              </motion.div>
            </div>
          </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Company Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {companyStats?.map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <Card
                className="text-center p-6 cursor-pointer"
              >
                              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={24} color="var(--color-primary)" />
                </div>
                <div className="text-3xl font-bold text-text-primary mb-2">{stat?.value}</div>
                <div className="text-sm font-semibold text-text-primary mb-1">{stat?.label}</div>
                <div className="text-xs text-text-secondary">{stat?.description}</div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-text-primary mb-4">Our Expert Team</h3>
          <p className="text-text-secondary mb-12 max-w-2xl mx-auto">
            Led by experienced medical professionals and industry experts committed to advancing healthcare in Malaysia.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers?.map((member, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  className="p-6 text-center cursor-pointer"
                >
                <div className="relative mb-4">
                  <Image
                    src={member?.image}
                    alt={member?.name}
                    className="w-24 h-24 rounded-full mx-auto object-cover shadow-modern"
                  />
                  <motion.div 
                    whileHover={{ scale: 1.1 }}
                    className="absolute -bottom-2 -right-2 w-8 h-8 bg-success rounded-full flex items-center justify-center shadow-modern"
                  >
                    <Icon name="CheckCircle" size={16} color="white" />
                  </motion.div>
                </div>
                <h4 className="text-lg font-semibold text-text-primary mb-1">{member?.name}</h4>
                <p className="text-primary font-medium text-sm mb-2">{member?.position}</p>
                <p className="text-text-secondary text-sm">{member?.expertise}</p>
              </Card>
            </motion.div>
          ))}
        </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;